var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { defineStore } from "pinia";
import { computed, del, ref, set } from "vue";
import { historyFetcher } from "@/api/histories";
import { archiveHistory, unarchiveHistory } from "@/api/histories.archived";
import { HistoryFilters } from "@/components/History/HistoryFilters";
import { useUserLocalStorage } from "@/composables/userLocalStorage";
import { cloneHistory, createAndSelectNewHistory, deleteHistoryById, getCurrentHistoryFromServer, getHistoryByIdFromServer, getHistoryCount, getHistoryList, secureHistoryOnServer, setCurrentHistoryOnServer, updateHistoryFields, } from "@/stores/services/history.services";
import { sortByObjectProp } from "@/utils/sorting";
const PAGINATION_LIMIT = 10;
const isLoadingHistory = new Set();
const CONTENT_STATS_KEYS = ["size", "contents_active", "update_time"];
export const useHistoryStore = defineStore("historyStore", () => {
    const historiesLoading = ref(false);
    const historiesOffset = ref(0);
    const totalHistoryCount = ref(0);
    const pinnedHistories = useUserLocalStorage("history-store-pinned-histories", []);
    const storedCurrentHistoryId = ref(null);
    const storedFilterTexts = ref({});
    const storedHistories = ref({});
    const histories = computed(() => {
        return Object.values(storedHistories.value)
            .filter((h) => !h.archived)
            .sort(sortByObjectProp("name"));
    });
    const getFirstHistoryId = computed(() => {
        var _a, _b;
        return (_b = (_a = histories.value[0]) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : null;
    });
    const currentHistory = computed(() => {
        if (storedCurrentHistoryId.value !== null) {
            return getHistoryById.value(storedCurrentHistoryId.value);
        }
        return null;
    });
    const currentHistoryId = computed(() => {
        if (storedCurrentHistoryId.value === null || !(storedCurrentHistoryId.value in storedHistories.value)) {
            return getFirstHistoryId.value;
        }
        else {
            return storedCurrentHistoryId.value;
        }
    });
    const currentFilterText = computed(() => {
        if (currentHistoryId.value) {
            return storedFilterTexts.value[currentHistoryId.value];
        }
        else {
            return "";
        }
    });
    /** Returns history from storedHistories, will load history if not in store */
    const getHistoryById = computed(() => {
        return (historyId) => {
            var _a;
            if (!storedHistories.value[historyId]) {
                // TODO: Try to remove this as it can cause computed side effects
                loadHistoryById(historyId);
            }
            return (_a = storedHistories.value[historyId]) !== null && _a !== void 0 ? _a : null;
        };
    });
    const getHistoryNameById = computed(() => {
        return (historyId) => {
            const history = getHistoryById.value(historyId);
            if (history) {
                return history.name;
            }
            else {
                return "...";
            }
        };
    });
    function setCurrentHistory(historyId) {
        return __awaiter(this, void 0, void 0, function* () {
            const currentHistory = (yield setCurrentHistoryOnServer(historyId));
            selectHistory(currentHistory);
            setFilterText(historyId, "");
        });
    }
    function setCurrentHistoryId(historyId) {
        storedCurrentHistoryId.value = historyId;
    }
    function setFilterText(historyId, filterText) {
        set(storedFilterTexts.value, historyId, filterText);
    }
    function setHistory(history) {
        if (storedHistories.value[history.id] !== undefined) {
            // Merge the incoming history with existing one to keep additional information
            Object.entries(history).forEach(([key, value]) => {
                set(storedHistories.value[history.id], key, value);
            });
        }
        else {
            set(storedHistories.value, history.id, history);
        }
    }
    function setHistories(histories) {
        // The incoming history list may contain less information than the already stored
        // histories, so we ensure that already available details are not getting lost.
        const enrichedHistories = histories.map((history) => {
            const historyState = storedHistories.value[history.id] || {};
            return Object.assign({}, historyState, history);
        });
        // Histories are provided as list but stored as map.
        const newMap = enrichedHistories.reduce((acc, h) => (Object.assign(Object.assign({}, acc), { [h.id]: h })), {});
        // Ensure that already stored histories, which are not available in the incoming array,
        // are not lost. This happens e.g. with shared histories since they have different owners.
        Object.values(storedHistories.value).forEach((history) => {
            const historyId = history.id;
            if (!newMap[historyId]) {
                newMap[historyId] = history;
            }
        });
        // Update stored histories
        storedHistories.value = newMap;
    }
    function setHistoriesLoading(loading) {
        historiesLoading.value = loading;
    }
    function pinHistory(historyId) {
        if (pinnedHistories.value.findIndex((item) => item.id == historyId) == -1) {
            pinnedHistories.value.push({ id: historyId });
        }
    }
    function unpinHistories(historyIds) {
        pinnedHistories.value = pinnedHistories.value.filter((h) => !historyIds.includes(h.id));
    }
    function selectHistory(history) {
        setHistory(history);
        setCurrentHistoryId(history.id);
    }
    function applyFilters(historyId, filters) {
        return __awaiter(this, void 0, void 0, function* () {
            if (currentHistoryId.value !== historyId) {
                yield setCurrentHistory(historyId);
            }
            const filterText = HistoryFilters.getFilterText(filters);
            setFilterText(historyId, filterText);
        });
    }
    function copyHistory(history, name, copyAll) {
        return __awaiter(this, void 0, void 0, function* () {
            const newHistory = (yield cloneHistory(history, name, copyAll));
            yield handleTotalCountChange(1);
            return setCurrentHistory(newHistory.id);
        });
    }
    function createNewHistory() {
        return __awaiter(this, void 0, void 0, function* () {
            const newHistory = (yield createAndSelectNewHistory());
            yield handleTotalCountChange(1);
            return selectHistory(newHistory);
        });
    }
    function getNextAvailableHistoryId(excludedIds) {
        const historyIds = Object.keys(storedHistories.value);
        const filteredHistoryIds = historyIds.filter((id) => !excludedIds.includes(id));
        return filteredHistoryIds[0];
    }
    function deleteHistory(historyId, purge) {
        return __awaiter(this, void 0, void 0, function* () {
            const deletedHistory = (yield deleteHistoryById(historyId, purge));
            const nextAvailableHistoryId = getNextAvailableHistoryId([deletedHistory.id]);
            if (nextAvailableHistoryId) {
                yield setCurrentHistory(nextAvailableHistoryId);
            }
            else {
                yield createNewHistory();
            }
            del(storedHistories.value, deletedHistory.id);
            unpinHistories([deletedHistory.id]);
            yield handleTotalCountChange(1, true);
        });
    }
    function loadCurrentHistory(since) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const history = (yield getCurrentHistoryFromServer(since));
                if (!history) {
                    return; // There are no changes to the current history, nothing to set
                }
                selectHistory(history);
                return history;
            }
            catch (error) {
                console.error(error);
            }
        });
    }
    /**
     * This function handles the cases where a history has been created
     * or removed (to set pagination offset and fetch updated history count)
     *
     * @param count How many histories have been added/removed
     * @param reduction Whether it is a reduction or addition (default)
     */
    function handleTotalCountChange(count = 0, reduction = false) {
        return __awaiter(this, void 0, void 0, function* () {
            historiesOffset.value += !reduction ? count : -count;
            yield loadTotalHistoryCount();
        });
    }
    function loadTotalHistoryCount() {
        return __awaiter(this, void 0, void 0, function* () {
            totalHistoryCount.value = yield getHistoryCount();
        });
    }
    /** TODO:
     * - not handling filters with pagination for now
     *   "pausing" pagination at the existing offset if a filter exists
     */
    function loadHistories(paginate = true, queryString) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!historiesLoading.value) {
                setHistoriesLoading(true);
                let limit = null;
                if (!queryString || queryString == "") {
                    if (paginate) {
                        yield loadTotalHistoryCount();
                        if (historiesOffset.value >= totalHistoryCount.value) {
                            setHistoriesLoading(false);
                            return;
                        }
                        limit = PAGINATION_LIMIT;
                    }
                    else {
                        historiesOffset.value = 0;
                    }
                }
                const offset = queryString ? 0 : historiesOffset.value;
                try {
                    const histories = (yield getHistoryList(offset, limit, queryString));
                    setHistories(histories);
                    if (paginate && !queryString && historiesOffset.value == offset) {
                        yield handleTotalCountChange(histories.length);
                    }
                }
                catch (error) {
                    console.error(error);
                }
                finally {
                    setHistoriesLoading(false);
                }
            }
        });
    }
    function loadHistoryById(historyId) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!isLoadingHistory.has(historyId)) {
                isLoadingHistory.add(historyId);
                try {
                    const history = (yield getHistoryByIdFromServer(historyId));
                    setHistory(history);
                    return history;
                }
                catch (error) {
                    console.error(error);
                }
                finally {
                    isLoadingHistory.delete(historyId);
                }
            }
        });
    }
    function secureHistory(history) {
        return __awaiter(this, void 0, void 0, function* () {
            const securedHistory = (yield secureHistoryOnServer(history));
            setHistory(securedHistory);
        });
    }
    function archiveHistoryById(historyId, archiveExportId, purgeHistory = false) {
        return __awaiter(this, void 0, void 0, function* () {
            const history = yield archiveHistory(historyId, archiveExportId, purgeHistory);
            setHistory(history);
            if (!history.archived) {
                return;
            }
            // If the current history is archived, we need to switch to another one as it is
            // no longer part of the active histories.
            const nextHistoryId = getNextAvailableHistoryId([historyId]);
            if (nextHistoryId) {
                return setCurrentHistory(nextHistoryId);
            }
            else {
                return createNewHistory();
            }
        });
    }
    function unarchiveHistoryById(historyId, force) {
        return __awaiter(this, void 0, void 0, function* () {
            const history = yield unarchiveHistory(historyId, force);
            setHistory(history);
            return history;
        });
    }
    function updateHistory(_a) {
        var { id } = _a, update = __rest(_a, ["id"]);
        return __awaiter(this, void 0, void 0, function* () {
            const savedHistory = (yield updateHistoryFields(id, update));
            setHistory(savedHistory);
        });
    }
    function updateContentStats(historyId) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const { data } = yield historyFetcher({
                    history_id: historyId,
                    keys: CONTENT_STATS_KEYS.join(","),
                });
                const contentStats = Object.assign({ id: historyId }, data);
                setHistory(contentStats);
                return contentStats;
            }
            catch (error) {
                console.error(error);
            }
        });
    }
    return {
        histories,
        currentHistory,
        currentHistoryId,
        currentFilterText,
        pinnedHistories,
        getHistoryById,
        getHistoryNameById,
        setCurrentHistory,
        setCurrentHistoryId,
        setFilterText,
        setHistory,
        setHistories,
        pinHistory,
        unpinHistories,
        selectHistory,
        applyFilters,
        copyHistory,
        createNewHistory,
        deleteHistory,
        loadCurrentHistory,
        loadHistories,
        loadHistoryById,
        secureHistory,
        updateHistory,
        archiveHistoryById,
        unarchiveHistoryById,
        historiesLoading,
        historiesOffset,
        totalHistoryCount,
        updateContentStats,
    };
});
